/**
 * Deadlines Page - Compliance & Deadlines Board
 * Firm-wide ATO lodgement deadlines across all clients.
 * Overdue first, with filter chips and per-client links.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  CalendarClock,
  AlertTriangle,
  Clock,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

type DeadlineStatus = 'overdue' | 'due_soon' | 'upcoming' | 'completed';

interface Deadline {
  id: string;
  clientId: string;
  clientName: string;
  clientType: string;
  title: string;
  description?: string;
  deadlineType: string;
  status: DeadlineStatus;
  dueDate: string;
  daysUntil: number;
  penalty?: string;
}

interface DeadlineStats {
  total: number;
  overdue: number;
  dueSoon: number;
  upcoming: number;
  completed: number;
}

type FilterKey = 'all' | 'overdue' | 'due_soon' | 'upcoming';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_soon', label: 'Due soon' },
  { key: 'upcoming', label: 'Upcoming' },
];

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const relativeDue = (daysUntil: number) => {
  if (daysUntil < 0) return `${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'} overdue`;
  if (daysUntil === 0) return 'Due today';
  return `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
};

const statusMeta = (status: DeadlineStatus) => {
  switch (status) {
    case 'overdue':
      return {
        Icon: AlertTriangle,
        text: 'text-red-600 dark:text-red-400',
        chip: 'bg-red-500/10 text-red-600 dark:text-red-400',
        border: 'border-red-500/20 bg-red-500/5',
      };
    case 'due_soon':
      return {
        Icon: Clock,
        text: 'text-amber-700 dark:text-amber-300',
        chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
        border: 'border-amber-500/10 bg-amber-500/5',
      };
    case 'completed':
      return {
        Icon: CheckCircle2,
        text: 'text-green-600 dark:text-green-400',
        chip: 'bg-green-500/10 text-green-600 dark:text-green-400',
        border: 'border-border bg-muted/50',
      };
    default:
      return {
        Icon: CalendarClock,
        text: 'text-green-600 dark:text-green-400',
        chip: 'bg-green-500/10 text-green-600 dark:text-green-400',
        border: 'border-border bg-muted/50',
      };
  }
};

export default function DeadlinesPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>('all');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['manager-deadlines'],
    queryFn: async () => {
      const r = await apiClient.get('/deadlines');
      return r.data.data as { deadlines: Deadline[]; stats: DeadlineStats };
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto">
        <h2 className="text-xl font-medium text-foreground">Deadlines</h2>
        <div className="mt-6 text-center py-12 bg-card border border-border rounded-xl">
          <AlertTriangle className="h-7 w-7 mx-auto text-red-600 dark:text-red-400" />
          <p className="mt-3 text-sm text-muted-foreground">Could not load deadlines.</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-lg bg-[var(--v4-accent)] text-white text-sm font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const stats = data?.stats;
  const deadlines = data?.deadlines || [];

  // Overdue first (handler already sorts, but keep it robust client-side).
  const order: Record<string, number> = { overdue: 0, due_soon: 1, upcoming: 2, completed: 3 };
  const filtered = deadlines
    .filter((d) => (filter === 'all' ? d.status !== 'completed' : d.status === filter))
    .sort((a, b) => {
      const s = (order[a.status] ?? 2) - (order[b.status] ?? 2);
      if (s !== 0) return s;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <CalendarClock className="h-6 w-6 text-[var(--v4-accent)]" />
        <div>
          <h2 className="text-xl font-medium text-foreground">Deadlines</h2>
          <p className="text-sm text-muted-foreground">Compliance & lodgement board</p>
        </div>
      </div>

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-red-600 dark:text-red-400">{stats.overdue}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Overdue</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{stats.dueSoon}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Due soon</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{stats.upcoming}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Upcoming</p>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium border transition',
              filter === f.key
                ? 'bg-[var(--v4-accent)] text-white border-transparent'
                : 'bg-muted/50 text-muted-foreground border-border'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-xl">
            <CheckCircle2 className="h-7 w-7 mx-auto text-green-600 dark:text-green-400" />
            <p className="mt-3 text-sm text-muted-foreground">
              {filter === 'all' ? 'No deadlines to show.' : 'Nothing in this group.'}
            </p>
          </div>
        ) : (
          filtered.map((d) => {
            const meta = statusMeta(d.status);
            const Icon = meta.Icon;
            return (
              <div
                key={d.id}
                onClick={() => d.clientId && navigate(`/pa/client/${d.clientId}`)}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-xl border transition',
                  meta.border,
                  d.clientId && 'cursor-pointer active:scale-[0.98]'
                )}
              >
                <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', meta.text)} />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{d.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{d.clientName}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', meta.chip)}>
                      {relativeDue(d.daysUntil)}
                    </span>
                    <span className="text-xs text-muted-foreground">{fmtDate(d.dueDate)}</span>
                  </div>
                </div>

                {d.clientId && (
                  <ChevronRight className="h-4 w-4 shrink-0 mt-1 text-muted-foreground" />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
